<template>
  <v-container
    id="data-tables"
    tag="section"
    fluid
  >
    <base-material-card
      color="indigo"
      icon="mdi-hand-coin"
      inline
      :class="['elevation-4', $vuetify.breakpoint.xs ? 'px-3 py-3' : 'px-5 py-4']"
    >
      <template v-slot:after-heading>
        <div :class="$vuetify.breakpoint.xs ? 'text-h4' : 'display-2'" class="font-weight-light">
          Withdrawal Requests (Companies)
        </div>
        <div class="text-body-2 grey--text mt-1">
          Review pending debit/withdrawal requests and mark them as paid or declined.
        </div>
      </template>

      <!-- Top toolbar -->
      <v-row class="mt-3 align-center">
        <v-col cols="12" md="6">
          <div class="d-flex align-center flex-wrap gap-8">
            <v-chip
              small
              :outlined="!showOnlyPending"
              :color="showOnlyPending ? 'indigo' : 'grey'"
              :text-color="showOnlyPending ? 'white' : 'grey darken-2'"
              @click="showOnlyPending = true"
            >
              Pending
            </v-chip>
            <v-chip
              small
              :outlined="showOnlyPending"
              :color="!showOnlyPending ? 'indigo' : 'grey'"
              :text-color="!showOnlyPending ? 'white' : 'grey darken-2'"
              @click="showOnlyPending = false"
            >
              All
            </v-chip>

            <v-chip small outlined color="indigo">
              {{ summary.companyCount }} companies
            </v-chip>
            <v-chip small outlined color="indigo">
              {{ summary.requestCount }} requests
            </v-chip>
            <v-chip small outlined color="warning">
              {{ summary.pendingCount }} pending
            </v-chip>
          </div>
        </v-col>

        <v-col cols="12" md="6" class="d-flex align-center justify-end">
          <v-btn
            small
            outlined
            color="indigo"
            :loading="loading"
            @click="refreshData"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </v-col>
      </v-row>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="indigo" size="56" width="5" />
        <div class="mt-4 text-body-2 grey--text">Loading withdrawal requests…</div>
      </div>

      <!-- Empty state -->
      <div v-else-if="items.length === 0" class="text-center py-12">
        <v-icon size="96" color="grey lighten-2" class="mb-4">mdi-domain-off</v-icon>
        <div class="text-h6 grey--text font-weight-light mb-2">
          No withdrawal requests
        </div>
        <div class="text-body-2 grey--text">
          When companies request withdrawals, they’ll appear here.
        </div>
      </div>

      <!-- Main (master-detail) -->
      <v-row v-else class="mt-4" dense>
        <!-- Company list -->
        <v-col cols="12" md="4">
          <v-card class="pane-card" outlined>
            <v-card-title class="pane-title">
              <v-icon left color="indigo">mdi-domain</v-icon>
              Companies
            </v-card-title>
            <v-card-text class="pt-3">
              <v-text-field
                v-model="companySearch"
                prepend-inner-icon="mdi-magnify"
                label="Search companies..."
                outlined
                dense
                clearable
                hide-details
                class="search-field mb-3"
              />

              <div v-if="filteredCompanyList.length === 0" class="text-center py-8 grey--text">
                <v-icon color="grey lighten-1" class="mb-2">mdi-domain-off</v-icon>
                <div class="text-body-2">No companies match your filters.</div>
              </div>

              <v-list v-else dense class="company-list">
                <v-list-item
                  v-for="c in filteredCompanyList"
                  :key="c.id"
                  :class="['company-item', selectedCompanyId === c.id ? 'company-item--active' : '']"
                  @click="selectCompany(c.id)"
                >
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2 font-weight-medium">
                      {{ c.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption grey--text">
                      {{ c.pendingCount }} pending • {{ c.totalCount }} total
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action class="d-flex flex-row align-center">
                    <v-chip x-small outlined color="warning" class="mr-1">{{ c.pendingCount }}</v-chip>
                    <v-chip x-small outlined color="indigo">{{ c.totalCount }}</v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Requests table -->
        <v-col cols="12" md="8">
          <v-card class="pane-card" outlined>
            <v-card-title class="pane-title d-flex align-center justify-space-between flex-wrap">
              <div class="d-flex align-center">
                <v-icon left color="indigo">mdi-receipt-text-outline</v-icon>
                <span class="mr-2">Requests</span>
                <span v-if="selectedCompany" class="grey--text text--darken-1 text-body-2">
                  — {{ selectedCompany.name }}
                </span>
              </div>

              <div class="d-flex align-center flex-wrap gap-8">
                <v-text-field
                  v-model="requestSearch"
                  prepend-inner-icon="mdi-magnify"
                  label="Search requests..."
                  outlined
                  dense
                  clearable
                  hide-details
                  class="search-field requests-search"
                />
              </div>
            </v-card-title>

            <v-divider />

            <v-card-text class="pt-4">
              <div v-if="!selectedCompany" class="text-center py-10 grey--text">
                <v-icon color="grey lighten-1" class="mb-2">mdi-cursor-default-click</v-icon>
                <div class="text-body-2">Select a company to view its requests.</div>
              </div>

              <v-data-table
                v-else
                :headers="headers"
                :items="selectedRequests"
                :search="requestSearch"
                class="modern-table"
                dense
                :mobile-breakpoint="0"
                :disable-pagination="$vuetify.breakpoint.xs"
                :hide-default-footer="$vuetify.breakpoint.xs"
              >
                <template v-slot:no-data>
                  <div class="text-center py-10 grey--text">
                    <v-icon color="grey lighten-1" class="mb-2">mdi-cash-remove</v-icon>
                    <div class="text-body-2">
                      {{ showOnlyPending ? 'No pending requests for this company.' : 'No requests for this company.' }}
                    </div>
                  </div>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    x-small
                    class="status-chip"
                    :color="item.status === 'paid' ? 'success' : item.status === 'declined' ? 'error' : 'warning'"
                    :text-color="item.status === 'paid' || item.status === 'declined' ? 'white' : 'black'"
                  >
                    {{ item.status || 'pending' }}
                  </v-chip>
                </template>

                <template v-slot:item.amount="{ item }">
                  <span class="amount-cell">{{ formatAmount(item.amount) }}</span>
                </template>

                <template v-slot:item.created_at="{ item }">
                  <span class="text-body-2">{{ formatRequestedOn(item.created_at) }}</span>
                </template>

                <template v-slot:item.actions="{ item }">
                  <div class="d-flex align-center justify-center">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="success"
                          class="mr-1 action-icon-btn"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="changeStatus(selectedCompany.id, item, 'paid')"
                        >
                          <v-icon small>mdi-check</v-icon>
                        </v-btn>
                      </template>
                      <span>Mark as Paid</span>
                    </v-tooltip>

                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="error"
                          class="action-icon-btn"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="changeStatus(selectedCompany.id, item, 'declined')"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </template>
                      <span>Mark as Declined</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </base-material-card>
    <base-material-snackbar
      v-model="snackbar"
      :type="snackbarType"
      v-bind="{
        bottom: true,
        right: true,
      }"
    >
      {{ snackbarText }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
  import {
    _getCompaniesDebitReqs,
    _updateDebitReqStatus,
  } from '@/services/Company'
  export default {
    name: 'CompanyModule',
    data: () => ({
      headers: [
        {
          text: 'ID',
          value: 'id',
        },
        {
          text: 'Payment Status',
          value: 'status',
        },
        {
          text: 'Requested On',
          value: 'created_at',
        },
        {
          text: 'Amount',
          value: 'amount',
        },
        {
          text: 'Actions',
          value: 'actions',
        },
      ],
      items: [],
      loading: false,
      companySearch: '',
      requestSearch: '',
      showOnlyPending: true,
      selectedCompanyId: null,
      snackbar: false,
      snackbarText: '',
      snackbarType: '',
    }),
    computed: {
      companiesView () {
        const isPending = this.isPending
        return (this.items || []).map((c) => {
          const wallet = Array.isArray(c.wallet) ? c.wallet : []
          return {
            ...c,
            totalCount: wallet.length,
            pendingCount: wallet.filter(isPending).length,
          }
        })
      },
      filteredCompanyList () {
        const q = (this.companySearch || '').toLowerCase().trim()
        return this.companiesView
          .filter((c) => {
            if (q && !(c.name && String(c.name).toLowerCase().includes(q))) return false
            // If "pending" filter is on, keep companies that have pending requests
            if (this.showOnlyPending) return c.pendingCount > 0
            return c.totalCount > 0
          })
          .sort((a, b) => (b.pendingCount - a.pendingCount) || (b.totalCount - a.totalCount))
      },
      selectedCompany () {
        if (!this.filteredCompanyList.length) return null
        const found = this.filteredCompanyList.find((c) => c.id === this.selectedCompanyId)
        return found || this.filteredCompanyList[0]
      },
      selectedRequests () {
        if (!this.selectedCompany) return []
        const wallet = Array.isArray(this.selectedCompany.wallet) ? this.selectedCompany.wallet : []
        return this.showOnlyPending ? wallet.filter(this.isPending) : wallet
      },
      summary () {
        const companies = this.items || []
        const allWallets = companies.flatMap((c) => (Array.isArray(c.wallet) ? c.wallet : []))
        const pendingCount = allWallets.filter(this.isPending).length
        return {
          companyCount: companies.length,
          requestCount: allWallets.length,
          pendingCount,
        }
      },
    },
    watch: {
      selectedCompany (val) {
        if (val && this.selectedCompanyId !== val.id) this.selectedCompanyId = val.id
      },
    },
    mounted () {
      this.refreshData()
    },
    methods: {
      refreshData () {
        this.loading = true
        _getCompaniesDebitReqs()
          .then(
            (res) => {
              this.items = res.data.obj.list
              this.loading = false
            },
            (error) => {
              this.loading = false
              console.log('error')
              console.log(error)
            }
          )
          .catch((error) => {
            this.loading = false
            console.log('catch')
            console.log(error)
          })
      },
      isPending (w) {
        return (w && w.status) ? !['paid', 'declined'].includes(String(w.status).toLowerCase()) : true
      },
      selectCompany (id) {
        this.selectedCompanyId = id
      },
      formatRequestedOn (value) {
        if (!value) return '—'
        const d = new Date(value)
        if (Number.isNaN(d.getTime())) return String(value)
        return d.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      },
      formatAmount (amount) {
        const n = Number(amount)
        if (Number.isNaN(n)) return '—'
        const value = n * -1
        return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },
      changeStatus (company, item, status) {
        _updateDebitReqStatus(item.id, { data: status })
          .then(
            (res) => {
              const selectedCompanyIndex = this.items.findIndex(
                (c) => c.id === company
              )
              this.items[selectedCompanyIndex].wallet = this.items[selectedCompanyIndex].wallet.filter(function (el) {
                return el.id !== item.id
              })
              this.snackbar = true
              this.snackbarType = 'success'
              this.snackbarText = res.data.responseMessage
            },
            (error) => {
              this.snackbar = true
              this.snackbarText = error.data.responseMessage
              this.snackbarType = 'error'
              console.log(error)
            }
          )
          .catch((error) => {
            console.log('catch')
            console.log(error)
          })
      },
    },
  }
</script>

<style scoped>
.gap-8 {
  gap: 8px;
}

.search-field >>> .v-input__control {
  border-radius: 8px;
}

.pane-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.pane-title {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-bottom: 1px solid #eeeeee;
  font-weight: 600;
}

.company-list {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eeeeee;
}

.company-item {
  transition: background 0.2s ease, transform 0.2s ease;
}

.company-item:hover {
  background: rgba(63, 81, 181, 0.04);
}

.company-item--active {
  background: rgba(63, 81, 181, 0.10);
}

.requests-search {
  min-width: 260px;
}

.modern-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.modern-table >>> thead th {
  font-weight: 600 !important;
  color: #424242 !important;
}

.modern-table >>> tbody tr:hover {
  background: rgba(63, 81, 181, 0.04);
}

.status-chip {
  text-transform: capitalize;
}

.amount-cell {
  font-weight: 600;
  color: #424242;
}

.action-icon-btn {
  box-shadow: none !important;
}
</style>
